import type {
  AddDelayType,
  AddPlayers,
  RankInfo
} from '@library/types/boardBuilder';
import type {
  BaseLeagueFilters,
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import {
  BoardTimePicker,
  StartTimePicker
} from '@library/components/simulations';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BoardBuilderProjectionRows } from '@bo-components/boardBuilder';
import type { DateTime } from 'luxon';
import type { FormattedGameMode } from '@library/types/gameModes';
import { LEAGUES_WITH_DISPLAY_TIME } from '@bo-constants/leagues';
import { NoBoardMessage } from '@library/components/boardBuilder';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { count } from 'ramda';
import { getErrorMessage } from '@bo-utils/common';
import { isStatUnavailableInDuration } from '@bo-utils/boardBuilderCreateProjections';
import { useBoardBuilderProjections } from '@bo-hooks/boardBuilder/useBoardBuilderProjections';

export interface BoardBuilderProjectionsProps {
  confirmedGameModes: Array<FormattedGameMode>;
  confirmedPlayersList: AddPlayers;
  confirmedStatTypes: Array<string>;
  durations: Array<LeagueProjectionAttributes>;
  gameStartTimeDelay: AddDelayType;
  gameStartTimes: Record<string, string>;
  hasSelections: boolean;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  onAddDelay: ({ gameId, delay }: AddDelayType) => void;
  onResetFilters: () => void;
  onResetRankInfo: () => void;
  onResetSearch: () => void;
  rankInfo: RankInfo;
  selectedDurationId: string;
  selectedLeagueDurationAttributes: BaseLeagueFilters;
  statTypes: Array<LeagueProjectionAttributes>;
}

export const BoardBuilderProjections = ({
  confirmedGameModes,
  confirmedPlayersList,
  confirmedStatTypes,
  durations,
  gameStartTimeDelay,
  gameStartTimes,
  hasSelections,
  leagueInfo,
  mainDurationId,
  onAddDelay,
  onResetFilters,
  onResetRankInfo,
  onResetSearch,
  rankInfo,
  selectedDurationId,
  selectedLeagueDurationAttributes,
  statTypes
}: BoardBuilderProjectionsProps) => {
  const [showSuccessBar, setShowSuccessBar] = useState(false);
  const isDisplayTimeVisible = useMemo(
    () => LEAGUES_WITH_DISPLAY_TIME.includes(leagueInfo.name),
    [leagueInfo.name]
  );

  const [bulkBoardTime, setBulkBoardTime] = useState<DateTime>();
  const [bulkStartTime, setBulkStartTime] = useState<DateTime>();
  const [bulkDisplayTime, setBulkDisplayTime] = useState<DateTime>();

  const {
    allProjections,
    filteredConfirmedGameModes,
    handleBulkAutoTradeChange,
    handleBulkChangeTime,
    handleProjectionsChange,
    handlePublishProjections,
    handleResetAll,
    handleToggleDurationStatTypeAllProjectionsStatus,
    isPublishProjectionsError,
    isPublishSuccess,
    projectionRows,
    publishProjectionsError,
    resetPublishProjectionsApi
  } = useBoardBuilderProjections({
    confirmedGameModes,
    confirmedPlayersList,
    confirmedStatTypes,
    durations,
    gameStartTimes,
    gameStartTimeDelay,
    leagueInfo,
    mainDurationId,
    onAddDelay,
    onResetFilters,
    onResetRankInfo,
    onResetSearch,
    rankInfo,
    selectedDurationId,
    statTypes
  });

  const errorMessage = isPublishProjectionsError
    ? getErrorMessage({
        isPublishProjectionsError,
        publishProjectionsError
      })
    : '';

  useEffect(() => {
    if (isPublishSuccess || isPublishProjectionsError) {
      setShowSuccessBar(true);
      resetPublishProjectionsApi();
    }
  }, [isPublishSuccess, isPublishProjectionsError, resetPublishProjectionsApi]);

  useEffect(() => {
    if (showSuccessBar && hasSelections) {
      setShowSuccessBar(false);
    }
  }, [hasSelections, showSuccessBar]);

  const confirmedStatTypesList = statTypes.filter(
    (statType) =>
      confirmedStatTypes.includes(statType.leagueProjectionFilterId) &&
      !isStatUnavailableInDuration({
        statType,
        leagueDurationAttributes: selectedLeagueDurationAttributes
      })
  );

  const totalChanges = allProjections.length && confirmedGameModes.length;
  const showSubmitResetBar = totalChanges > 0;

  const saveDisabled = allProjections.some(
    (projection) =>
      projection.status === 'pre_game' && !projection.projectionValue
  );

  const activeProjections = allProjections.filter(
    (projection) => projection.status === 'pre_game'
  );

  const handleSaveProjections = async () => {
    await handlePublishProjections({
      leagueId: leagueInfo.id,
      projections: activeProjections
    });
  };

  const errorCount = count(
    (row) => row.status === 'pre_game' && !!row.errors?.length,
    allProjections
  );

  const handleBulkTimeChange = useCallback(
    (value: DateTime, key: string, setter: (value: DateTime) => void) => {
      if (value) {
        setter(value);

        handleBulkChangeTime({
          isTopLevelChange: true,
          [key]: value
        });
      }
    },
    [handleBulkChangeTime]
  );

  const handleBulkBoardTimeChange = useCallback(
    (boardTime: DateTime) => {
      handleBulkTimeChange(boardTime, 'boardTime', setBulkBoardTime);
    },
    [handleBulkTimeChange, setBulkBoardTime]
  );

  const handleBulkStartTimeChange = useCallback(
    (startTime: DateTime) => {
      handleBulkTimeChange(startTime, 'startTime', setBulkStartTime);
    },
    [handleBulkTimeChange, setBulkStartTime]
  );

  const handleBulkDisplayTimeChange = useCallback(
    (displayTime: DateTime) => {
      handleBulkTimeChange(displayTime, 'displayTime', setBulkDisplayTime);
    },
    [handleBulkTimeChange, setBulkDisplayTime]
  );

  if (!totalChanges) {
    return (
      <>
        {showSuccessBar && (
          <div className="bg-soFresh-130 border-soFresh-140 my-4 flex h-12 w-full items-center justify-start gap-2 rounded border">
            <p className="text-atlien-200 p-6">
              Projections Published Successfully
            </p>
          </div>
        )}
        <NoBoardMessage />
      </>
    );
  }

  const variableBoardTime =
    bulkBoardTime?.toISO() || allProjections[0].boardTime;

  const variableStartTime =
    bulkStartTime?.toISO() || allProjections[0].startTime;

  const variableDisplayTime =
    bulkDisplayTime?.toISO() ||
    allProjections[0].displayTime ||
    allProjections[0].startTime;

  return (
    <>
      {showSubmitResetBar && (
        <SubmitResetBar
          className="mb-4"
          cancelText="Reset Changes"
          handlePublish={handleSaveProjections}
          handleReset={handleResetAll}
          prefix={
            <>
              <span className="text-soClean-100 pl-6 text-sm">{`${
                activeProjections.length
              } Active projection${totalChanges > 1 ? 's' : ''}`}</span>
              {errorCount > 0 && (
                <span className="text-roses">{`${errorCount} Projection(s) have invalid data!`}</span>
              )}
              {isPublishProjectionsError && (
                <p className="text-roses p-6">{`Error: ${
                  errorMessage ?? ''
                }`}</p>
              )}
            </>
          }
          saveDisabled={saveDisabled}
        />
      )}
      <span className="flex justify-end gap-4">
        <Tooltip text="Change Board Time for all below projections">
          <div className="mb-4 flex items-center gap-2">
            <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
              Bulk Board Time
            </span>

            <BoardTimePicker
              boardTime={variableBoardTime}
              handleChange={handleBulkBoardTimeChange}
              initialBoardTime={allProjections[0].boardTime}
              startTime={variableStartTime}
            />
          </div>
        </Tooltip>
        <Tooltip text="Change Start Time for all below projections">
          <div className="mb-4 flex items-center gap-2">
            <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
              Bulk Start Time
            </span>

            <StartTimePicker
              handleChange={handleBulkStartTimeChange}
              initialStartTime={allProjections[0].startTime}
              startTime={variableStartTime}
            />
          </div>
        </Tooltip>
        {isDisplayTimeVisible && (
          <Tooltip text="Change Display Time for all below projections">
            <div className="mb-4 flex items-center gap-2">
              <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
                Bulk Display Time
              </span>

              <StartTimePicker
                handleChange={handleBulkDisplayTimeChange}
                initialStartTime={allProjections[0].displayTime}
                startTime={variableDisplayTime}
              />
            </div>
          </Tooltip>
        )}
      </span>
      <div className="bg-soFresh-110 border-soFresh-140 flex max-h-[600px] w-full flex-col overflow-scroll pb-1 pl-0">
        <div className="flex size-full flex-col gap-4">
          {confirmedStatTypesList.map((confirmedStatType) => {
            const {
              leagueProjectionFilterId: statTypeId,
              leagueProjectionFilterName: statTypeName
            } = confirmedStatType;

            const projections = projectionRows
              ? (projectionRows[statTypeId]?.projections ?? [])
              : [];

            return (
              <BoardBuilderProjectionRows
                confirmedGameModes={filteredConfirmedGameModes}
                durations={durations}
                key={`${selectedDurationId}-${statTypeId}`}
                leagueInfo={leagueInfo}
                mainDurationId={mainDurationId}
                projectionRows={projections}
                selectedDurationId={selectedDurationId}
                onProjectionsChange={handleProjectionsChange}
                onToggleDurationStatTypeAllProjectionsStatus={
                  handleToggleDurationStatTypeAllProjectionsStatus
                }
                onBulkAutoTradeChange={handleBulkAutoTradeChange}
                onBulkTimeChange={handleBulkChangeTime}
                statTypeId={statTypeId}
                statTypeName={statTypeName}
                isDisplayTimeVisible={isDisplayTimeVisible}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
